import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchArticles, saveArticlesToSupabase, migrateNewsToSupabase, uploadImageToStorage, deleteArticle, type NewsArticle } from '@/utils/adminStore';
import RichTextEditor from '@/components/base/RichTextEditor';

const categoryOptions = [
  { value: 'brand', label: '品牌新闻' },
  { value: 'training', label: '培训赋能' },
  { value: 'store', label: '门店动态' },
  { value: 'franchise', label: '招商资讯' },
  { value: 'health', label: '健康知识' },
];

const emptyArticle: NewsArticle = {
  id: 0,
  title: '',
  date: new Date().toISOString().split('T')[0],
  category: 'brand',
  summary: '',
  image: '',
  featured: false,
  content_blocks: [],
  content_html: '',
  sort_order: 0,
  created_at: '',
  updated_at: '',
};

export default function NewsManagement() {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form, setForm] = useState<NewsArticle>(emptyArticle);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setLoadError(null);
    try {
      await migrateNewsToSupabase();
      const data = await fetchArticles();
      setArticles(data);
    } catch {
      setLoadError('加载资讯失败');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const syncAndSave = async (updated: NewsArticle[]) => {
    const previousArticles = articles;
    setSaveError(null);
    setSaving(true);
    setArticles(updated);
    setSelected(new Set());
    const ok = await saveArticlesToSupabase(updated);
    if (ok) {
      await loadData(false);
    } else {
      setArticles(previousArticles);
      setSaveError('保存失败，可能是网络问题或数据过大，请稍后重试');
    }
    setSaving(false);
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(articles.map((a) => a.id)));
  };

  const deselectAll = () => {
    setSelected(new Set());
  };

  const handleBatchDelete = async () => {
    setBatchDeleteConfirm(false);
    setSaveError(null);
    setSaving(true);

    const idsToDelete = Array.from(selected);
    let allOk = true;
    for (const id of idsToDelete) {
      const ok = await deleteArticle(id);
      if (!ok) {
        allOk = false;
        break;
      }
    }

    if (allOk) {
      setSelected(new Set());
      await loadData(false);
    } else {
      setSaveError('批量删除部分失败，请刷新后重试');
    }
    setSaving(false);
  };

  const getNextId = (): number => {
    if (articles.length === 0) return 1;
    return Math.max(...articles.map((a) => a.id)) + 1;
  };

  const openAdd = () => {
    setEditingIndex(null);
    setForm({ ...emptyArticle, id: getNextId() });
    setShowModal(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setForm({ ...articles[index] });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.summary.trim()) return;

    const updated = [...articles];
    if (editingIndex !== null) {
      updated[editingIndex] = form;
    } else {
      updated.push(form);
    }
    syncAndSave(updated);
    setShowModal(false);
  };

  const handleDelete = async (index: number) => {
    const articleToDelete = articles[index];
    setDeleteConfirm(null);
    setSaveError(null);
    setSaving(true);

    const ok = await deleteArticle(articleToDelete.id);
    if (ok) {
      await loadData(false);
    } else {
      setSaveError('删除失败，请稍后重试');
    }
    setSaving(false);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...articles];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    syncAndSave(updated);
  };

  const moveDown = (index: number) => {
    if (index === articles.length - 1) return;
    const updated = [...articles];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    syncAndSave(updated);
  };

  const updateField = (field: keyof NewsArticle, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const categoryLabel = (key: string) => {
    const found = categoryOptions.find((c) => c.value === key);
    return found?.label || key;
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverUploading(true);
    const url = await uploadImageToStorage(file, 'covers');
    if (url) {
      updateField('image', url);
    }
    setCoverUploading(false);
  };

  const triggerCoverUpload = () => {
    coverInputRef.current?.click();
  };

  const removeCover = () => {
    updateField('image', '');
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const isAllSelected = articles.length > 0 && selected.size === articles.length;
  const isSomeSelected = selected.size > 0;

  return (
    <div>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold mb-3" />
          <p className="text-sm text-gray-400">正在加载资讯数据...</p>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16">
          <p className="text-sm text-gray-500 mb-4">{loadError}</p>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 px-7 py-3 bg-brand-light text-brand-dark text-sm font-medium rounded-full hover:bg-brand-cream transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重试
          </button>
        </div>
      ) : (
        <>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-brand-deep">资讯管理</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            管理品牌动态页面的资讯内容（共 {articles.length} 篇）
            {isSomeSelected && (
              <span className="ml-2 text-brand-brown font-medium">已选 {selected.size} 篇</span>
            )}
            {saving && (
              <span className="ml-2 text-amber-500 font-medium">
                <i className="ri-loader-4-line animate-spin mr-0.5" />
                保存中...
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSomeSelected && (
            <button
              onClick={() => setBatchDeleteConfirm(true)}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line" />
              批量删除 ({selected.size})
            </button>
          )}
          {isSomeSelected ? (
            <button
              onClick={deselectAll}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-brand-cream text-gray-500 rounded-lg hover:bg-brand-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line" />
              取消选择
            </button>
          ) : (
            <button
              onClick={openAdd}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-brand-deep text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              新增资讯
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-brand-light/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-light/30 bg-brand-gray">
                <th className="text-center px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={() => isAllSelected ? deselectAll() : selectAll()}
                    className="w-4 h-4 rounded border-brand-cream text-brand-deep focus:ring-brand-warm cursor-pointer"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 w-12">#</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 w-14">封面</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400">标题</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 hidden md:table-cell w-24">分类</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 hidden lg:table-cell w-28">日期</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 hidden xl:table-cell">摘要</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 w-36">操作</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a, i) => (
                <tr key={a.id} className={`border-b border-brand-light/20 hover:bg-brand-gray/50 transition-colors ${selected.has(a.id) ? 'bg-brand-light/40' : ''}`}>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(a.id)}
                      onChange={() => toggleSelect(a.id)}
                      className="w-4 h-4 rounded border-brand-cream text-brand-deep focus:ring-brand-warm cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{i + 1}</td>
                  <td className="px-4 py-3">
                    {a.image ? (
                      <img
                        src={a.image}
                        alt={a.title}
                        className="w-9 h-9 rounded object-cover object-top border border-brand-light"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded bg-brand-light flex items-center justify-center">
                        <i className="ri-image-line text-sm text-gray-300" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-brand-deep line-clamp-1 max-w-[280px]">{a.title}</span>
                      {a.featured && (
                        <span className="shrink-0 text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium">
                          精选
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-xs px-2 py-0.5 bg-brand-light text-brand-dark rounded-full">
                      {categoryLabel(a.category)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 hidden lg:table-cell text-xs">{a.date}</td>
                  <td className="px-4 py-3 text-gray-400 hidden xl:table-cell max-w-[240px] truncate text-xs">{a.summary}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => moveUp(i)}
                        disabled={i === 0 || saving}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-brand-deep hover:bg-brand-light disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        title="上移"
                      >
                        <i className="ri-arrow-up-s-line text-sm" />
                      </button>
                      <button
                        onClick={() => moveDown(i)}
                        disabled={i === articles.length - 1 || saving}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-brand-deep hover:bg-brand-light disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        title="下移"
                      >
                        <i className="ri-arrow-down-s-line text-sm" />
                      </button>
                      <button
                        onClick={() => openEdit(i)}
                        disabled={saving}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-brand-deep hover:bg-brand-light disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        title="编辑"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(i)}
                        disabled={saving}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        title="删除"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {articles.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-300 text-sm">
                    暂无资讯数据，点击"新增资讯"添加
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {saveError && (
        <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-red-600">
            <i className="ri-error-warning-line" />
            <span>{saveError}</span>
          </div>
          <button
            onClick={() => setSaveError(null)}
            className="text-red-400 hover:text-red-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Edit/Add Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[4vh]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[94vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-brand-light/30 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="text-base font-semibold text-brand-deep">
                {editingIndex !== null ? '编辑资讯' : '新增资讯'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-brand-deep hover:bg-brand-light transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Basic Info */}
              <div>
                <label className="block text-xs font-medium text-brand-deep mb-1">标题 *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep placeholder:text-gray-300 focus:outline-none focus:border-brand-warm transition-colors"
                  placeholder="资讯标题..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-brand-deep mb-1">分类</label>
                  <select
                    value={form.category}
                    onChange={(e) => updateField('category', e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep focus:outline-none focus:border-brand-warm transition-colors cursor-pointer"
                  >
                    {categoryOptions.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-brand-deep mb-1">日期</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => updateField('date', e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep focus:outline-none focus:border-brand-warm transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => updateField('featured', e.target.checked)}
                    className="w-4 h-4 rounded border-brand-cream text-brand-deep focus:ring-brand-warm cursor-pointer"
                  />
                  <span className="text-xs font-medium text-brand-deep">标记为精选</span>
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-deep mb-1">摘要 *</label>
                <textarea
                  value={form.summary}
                  onChange={(e) => updateField('summary', e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-brand-light bg-brand-gray text-brand-deep placeholder:text-gray-300 focus:outline-none focus:border-brand-warm transition-colors resize-none"
                  placeholder="资讯摘要..."
                />
                <p className="text-[10px] text-gray-300 mt-1 text-right">{form.summary.length}/500</p>
              </div>

              {/* Cover Image Upload */}
              <div>
                <label className="block text-xs font-medium text-brand-deep mb-2">封面图片</label>
                <div className="flex items-start gap-4">
                  <div
                    className="w-28 h-20 rounded-xl border-2 border-dashed border-brand-cream flex items-center justify-center overflow-hidden bg-brand-gray shrink-0 cursor-pointer hover:border-brand-warm transition-colors"
                    onClick={triggerCoverUpload}
                  >
                    {form.image ? (
                      <img src={form.image} alt="封面预览" className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-gray-300">
                        {coverUploading ? (
                          <i className="ri-loader-4-line animate-spin text-lg" />
                        ) : (
                          <>
                            <i className="ri-camera-line text-lg" />
                            <span className="text-[10px]">上传</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={triggerCoverUpload}
                      className="px-3 py-1.5 text-xs font-medium border border-brand-cream text-brand-deep rounded-lg hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-upload-2-line mr-1" />
                      选择图片
                    </button>
                    {form.image && (
                      <button
                        type="button"
                        onClick={removeCover}
                        className="px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-1" />
                        移除图片
                      </button>
                    )}
                    <p className="text-[10px] text-gray-300">支持 JPG/PNG，不超过 1MB</p>
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleCoverUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Rich Text Editor */}
              <div className="border-t border-brand-light/30 pt-5">
                <label className="block text-xs font-medium text-brand-deep mb-3">正文内容</label>
                <RichTextEditor
                  initialHtml={form.content_html || ''}
                  onChange={(html) => setForm((prev) => ({ ...prev, content_html: html }))}
                  onUploadImage={async (file) => await uploadImageToStorage(file, 'blocks')}
                />
                <p className="text-[10px] text-gray-300 mt-2">
                  支持加粗、斜体、标题、列表、链接和插入图片，图片会自动上传到云端存储
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-brand-light/30 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-brand-deep hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || !form.summary.trim() || saving}
                className="px-5 py-2 text-sm font-medium bg-brand-deep text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1" />
                    保存中...
                  </>
                ) : editingIndex !== null ? (
                  '保存修改'
                ) : (
                  '添加资讯'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50 text-red-500">
                <i className="ri-error-warning-line text-xl" />
              </div>
              <h3 className="text-base font-semibold text-brand-deep mb-1">确认删除</h3>
              <p className="text-sm text-gray-400 mb-6">
                确定要删除资讯「{articles[deleteConfirm]?.title.slice(0, 20)}...」吗？此操作不可撤销。
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-400 hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Delete Confirmation */}
      {batchDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setBatchDeleteConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50 text-red-500">
                <i className="ri-error-warning-line text-xl" />
              </div>
              <h3 className="text-base font-semibold text-brand-deep mb-1">批量删除确认</h3>
              <p className="text-sm text-gray-400 mb-6">
                确定要删除选中的 {selected.size} 篇资讯吗？此操作不可撤销。
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setBatchDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-400 hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  onClick={handleBatchDelete}
                  className="px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  确认删除 {selected.size} 篇
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}